import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Globe } from 'lucide-react';
import { Btn } from '@/presentation/component/ui';
import { useAuth } from '@/presentation/hook/useAuth';

const NAV_LINKS = [
  { to: '/', key: 'nav.home' },
  { to: '/formation', key: 'nav.formations' },
  { to: '/contact', key: 'nav.contact' },
] as const;

export const MainNavbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const isActive = (path: string) => location.pathname === path;

  const toggleLanguage = () => {
    const next = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(next);
  };

  return (
    <nav className="bg-[var(--color-surface)] shadow-sm border-b border-[var(--color-line)] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
              </svg>
            </div>
            <span className="text-xl font-bold text-[var(--color-ink)] hidden sm:block">
              DaaraJi
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map(({ to, key }) => (
              <Link
                key={to}
                to={to}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(to)
                    ? 'text-[var(--color-accent)] bg-[var(--color-accent-soft)]'
                    : 'text-[var(--color-ink-soft)] hover:text-[var(--color-accent)] hover:bg-[var(--color-surface-2)]'
                }`}
              >
                {t(key)}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggleLanguage}
              className="p-2 rounded-md text-[var(--color-ink-soft)] hover:text-[var(--color-accent)] hover:bg-[var(--color-surface-2)] transition-colors"
              aria-label={t('nav.language', { defaultValue: 'Change language' })}
            >
              <Globe className="h-5 w-5" />
            </button>

            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-[var(--color-ink-soft)]">
                  {t('nav.hello')}, {user?.name}
                </span>
                <Btn size="sm" onClick={logout}>
                  {t('nav.logout')}
                </Btn>
              </div>
            ) : (
              <>
                <Link to="/login">
                  <Btn kind="ghost" size="sm">
                    {t('nav.login')}
                  </Btn>
                </Link>
                <Link to="/signup">
                  <Btn kind="primary" size="sm">
                    {t('nav.signup')}
                  </Btn>
                </Link>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={toggleLanguage}
              className="p-2 rounded-md text-[var(--color-ink-soft)] hover:text-[var(--color-accent)]"
              aria-label={t('nav.language', { defaultValue: 'Change language' })}
            >
              <Globe className="h-5 w-5" />
            </button>
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md text-[var(--color-ink-soft)] hover:text-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-tint)]"
              aria-label={t('nav.menu', { defaultValue: 'Menu' })}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden py-2">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-[var(--color-surface-2)] rounded-lg">
              {NAV_LINKS.map(({ to, key }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 text-base font-medium text-[var(--color-ink-soft)] hover:text-[var(--color-accent)] hover:bg-[var(--color-surface-3)] rounded-md"
                >
                  {t(key)}
                </Link>
              ))}

              {isAuthenticated ? (
                <div className="border-t border-[var(--color-line)] pt-3 mt-3">
                  <div className="px-3 py-2 text-sm text-[var(--color-ink-soft)]">
                    {t('nav.hello')}, {user?.name}
                  </div>
                  <button
                    onClick={() => {
                      logout();
                      closeMobileMenu();
                    }}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-[var(--color-ink-soft)] hover:text-[var(--color-accent)] hover:bg-[var(--color-surface-3)] rounded-md"
                  >
                    {t('nav.logout')}
                  </button>
                </div>
              ) : (
                <div className="border-t border-[var(--color-line)] pt-3 mt-3 space-y-2">
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-base font-medium text-[var(--color-ink-soft)] hover:text-[var(--color-accent)] hover:bg-[var(--color-surface-3)] rounded-md"
                  >
                    {t('nav.login')}
                  </Link>
                  <Link
                    to="/signup"
                    onClick={closeMobileMenu}
                    className="block mx-3 px-3 py-2 bg-[var(--color-accent)] text-[var(--color-on-accent)] font-medium rounded-md hover:bg-[var(--color-accent-hover)] text-center"
                  >
                    {t('nav.signup')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
